import React from "react";
import { Thermometer, Fan, Activity, Gauge as GaugeIcon, Wind } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { StatCard } from "../common/StatCard";
import { Gauge } from "../common/Gauge";
import { PhaseMini } from "../common/PhaseMini";
import { LcdTerminal } from "../common/LcdTerminal";
import { Panel, Overline } from "../common/Panel";
import { fmt } from "../../lib/api";

const tempColor  = (v) => (v >= 52 ? "var(--danger)" : v >= 44 ? "var(--warning)" : "var(--accent)");
const powerColor = (v) => (v >= 4500 ? "var(--danger)" : v >= 3000 ? "var(--warning)" : "var(--accent)");
const gasColor   = (q) => (q === "DANGER" ? "var(--danger)" : q === "POOR" ? "var(--warning)" : "var(--accent)");

export default function Dashboard() {
  const { telemetry: t } = useApp();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Overline>Live Telemetry</Overline>
          <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Sensor Overview</h2>
        </div>
        <div className="text-right hidden sm:block">
          <div className="text-[10px] uppercase tracking-wider text-[var(--t2)]">Sample Rate</div>
          <div className="font-mono text-sm">2.0 Hz</div>
        </div>
      </div>

      {/* stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard icon={Thermometer} label="Temperature" value={fmt(t?.temperature, 1)} unit="°C" accent={t ? tempColor(t.temperature) : "var(--accent)"} sub="DS18B20 probe" testid="stat-temp" />
        <StatCard icon={Fan} label="Fan Speed" value={fmt(t?.fan_speed, 0)} unit="%" sub="PWM cooling" testid="stat-fan" />
        <StatCard icon={Activity} label={t ? (t.pir ? "Motion + Light" : "PIR / Light") : "PIR / Light"} value={t ? (t.pir ? "DETECT" : "IDLE") : "--"} sub={t ? `LED ${t.light ? "ON" : "OFF"}` : "AM312 sensor"} testid="stat-pir" />
        <StatCard icon={GaugeIcon} label="Total Power" value={fmt(t?.total_power, 0)} unit="W" accent={t ? powerColor(t.total_power) : "var(--accent)"} sub="L1 + L2 + L3" testid="stat-power" />
        <StatCard icon={Wind} label="Air Quality" value={t?.gas_quality ?? "--"} unit="" accent={t ? gasColor(t.gas_quality) : "var(--accent)"} sub={t?.gas_ppm != null ? `${Math.round(t.gas_ppm)} ppm` : "MQ-135"} testid="stat-gas" />
      </div>

      {/* phase mini-cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PhaseMini name="L1" data={t?.phases?.L1} testid="phase-mini-l1" />
        <PhaseMini name="L2" data={t?.phases?.L2} testid="phase-mini-l2" />
        <PhaseMini name="L3" data={t?.phases?.L3} testid="phase-mini-l3" />
      </div>

      {/* gauges + LCD */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="p-6 lg:col-span-2">
          <Overline>Real-time Gauges</Overline>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
            <Gauge value={t?.temperature ?? null} max={80} label="Temp" unit="°C" colorFor={tempColor} testid="gauge-temp" />
            <Gauge value={t?.fan_speed ?? null} max={100} label="Fan" unit="%" testid="gauge-fan" />
            <Gauge value={t?.total_power ?? null} max={6000} label="Power" unit="W" colorFor={powerColor} testid="gauge-power" />
            <Gauge value={t ? t.power_factor * 100 : null} max={100} label="Pwr Factor" unit="%" testid="gauge-pf" />
          </div>
        </Panel>

        <div className="space-y-3">
          <Overline>Physical Display</Overline>
          <LcdTerminal telemetry={t} testid="lcd-terminal" />
          <p className="text-xs text-[var(--t2)] font-mono">Mirrors the on-device 16×2 character LCD in real time.</p>
        </div>
      </div>
    </div>
  );
}
